/**
 * @typedef {{ name: string, address?: string, city?: string, state?: string, postal_code?: string, phone?: string, maps_url?: string, hours?: string, latitude?: number, longitude?: number, brand?: string }} StoreData
 * @typedef {{ store: StoreData, distance: number }} NearbyStore
 */

const LOCATION_CACHE_KEY = 'mut_user_location';
const LOCATION_TTL_MS    = 30 * 24 * 60 * 60 * 1000; // 30 days
const NEARBY_RADIUS_KM   = 10;
const PAGE_SIZE          = 10;

class MutStoreFinder extends HTMLElement {
  /** @type {{ lat: number, lng: number } | null} */
  _userCoords = null;

  /** @type {'location' | 'city'} */
  _mode = 'location';

  /** @type {StoreData[] | null} */
  _allStores = null;

  /** @type {NearbyStore[]} */
  _nearbyStores = [];

  _locationPage = 1;
  _locationTotalPages = 1;

  _currentPage = 1;
  _totalPages  = 1;
  _currentCity = '';
  _currentState = '';
  _apiUrl      = '';

  /** @type {Record<string, { url: string, alt: string }>} */
  _brandLogos = {};

  /** @type {Record<string, string>} */
  _i18n = {};

  connectedCallback() {
    /** @type {Record<string, HTMLElement>} */
    this._refs = {};
    for (const el of this.querySelectorAll('[ref]')) {
      this._refs[el.getAttribute('ref')] = /** @type {HTMLElement} */ (el);
    }

    try {
      this._brandLogos = JSON.parse(this._refs.brandLogos?.textContent || '{}');
    } catch { this._brandLogos = {}; }

    try {
      this._i18n = JSON.parse(this._refs.i18n?.textContent || '{}');
    } catch { this._i18n = {}; }

    for (const el of this.querySelectorAll('[on\\:change]')) {
      const method = el.getAttribute('on:change').slice(1);
      el.addEventListener('change', (e) => this[method]?.(e));
    }

    for (const el of this.querySelectorAll('[on\\:click]')) {
      const method = el.getAttribute('on:click').slice(1);
      el.addEventListener('click', (e) => this[method]?.(e));
    }

    this._apiUrl = (this.dataset.api ?? '').replace(/\/+$/, '');
    if (this._apiUrl) {
      this.#fetchStates();
      this.#fetchCities();
      this.#initGeolocation();
    }
  }

  // ─── Geolocation ────────────────────────────────────────────────────────────

  #initGeolocation() {
    if (!navigator.geolocation) {
      this.#fetchPage(); // fallback: paginated all stores
      return;
    }

    try {
      const cached = localStorage.getItem(LOCATION_CACHE_KEY);
      if (cached) {
        const { lat, lng, expires } = JSON.parse(cached);
        if (Date.now() < expires) {
          this._userCoords = { lat, lng };
          this.#activateLocationMode(lat, lng);
          return;
        }
        localStorage.removeItem(LOCATION_CACHE_KEY);
      }
    } catch { /* ignore */ }

    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        const { latitude: lat, longitude: lng } = coords;
        this._userCoords = { lat, lng };
        try {
          localStorage.setItem(LOCATION_CACHE_KEY, JSON.stringify({ lat, lng, expires: Date.now() + LOCATION_TTL_MS }));
        } catch { /* ignore */ }
        this.#activateLocationMode(lat, lng);
      },
      () => this.#fetchPage(), // denied: fallback to paginated
      { timeout: 10000 }
    );
  }

  /** @param {number} lat @param {number} lng */
  async #activateLocationMode(lat, lng) {
    this._mode = 'location';
    this._locationPage = 1;

    const loading = this._refs.loadingState;
    if (loading) loading.hidden = false;

    try {
      if (!this._allStores) {
        const resp = await fetch(`${this._apiUrl}?all=true`);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const { stores } = await resp.json();
        this._allStores = stores;
      }

      this._nearbyStores = this._allStores
        .map((store) => {
          const distance = (store.latitude != null && store.longitude != null)
            ? this.#haversine(lat, lng, store.latitude, store.longitude)
            : Infinity;
          return { store, distance };
        })
        .filter(({ distance }) => isFinite(distance) && distance <= NEARBY_RADIUS_KM)
        .sort((a, b) => a.distance - b.distance);

      // No store within the radius: fall back to browse mode so the user isn't
      // left on a dead-end "no results" screen (sparse coverage is common).
      if (this._nearbyStores.length === 0) {
        this.#fetchPage();
        return;
      }

      this._locationTotalPages = Math.max(1, Math.ceil(this._nearbyStores.length / PAGE_SIZE));

      if (loading) loading.hidden = true;
      this.#renderLocationPage();
    } catch {
      if (loading) loading.hidden = true;
      const error = this._refs.errorState;
      if (error) error.hidden = false;
    }
  }

  #renderLocationPage() {
    const list     = this._refs.storeList;
    const template = this._refs.cardTemplate;
    if (!list || !(template instanceof HTMLTemplateElement)) return;

    const start = (this._locationPage - 1) * PAGE_SIZE;
    const page  = this._nearbyStores.slice(start, start + PAGE_SIZE);

    list.innerHTML = '';
    const fragment = document.createDocumentFragment();

    for (const { store, distance } of page) {
      const card = this.#buildCard(template, store);
      if (!card) continue;
      const distEl = /** @type {HTMLElement | null} */ (card.querySelector('.mut-store-card__distance'));
      if (distEl) {
        distEl.textContent = distance < 1
          ? (this._i18n.distanceM ?? '').replace('__DIST__', (distance * 1000).toFixed(0))
          : (this._i18n.distanceKm ?? '').replace('__DIST__', distance.toFixed(1));
        distEl.hidden = false;
      }
      fragment.append(card);
    }

    list.append(fragment);

    const total = this._nearbyStores.length;

    if (this._refs.resultCount) {
      const template = total === 1 ? this._i18n.countWithinOne : this._i18n.countWithinOther;
      this._refs.resultCount.textContent = total > 0
        ? (template ?? '').replace('__COUNT__', String(total)).replace('__RADIUS__', String(NEARBY_RADIUS_KM))
        : '';
    }

    if (this._refs.noResults) {
      this._refs.noResults.hidden = total > 0;
    }

    // Sync pagination refs
    this._currentPage = this._locationPage;
    this._totalPages  = this._locationTotalPages;
    this.#updatePagination();
  }

  // ─── City mode ──────────────────────────────────────────────────────────────

  async #fetchPage() {
    this._mode = 'city';
    const loading = this._refs.loadingState;
    const error   = this._refs.errorState;
    if (loading) loading.hidden = false;
    if (error)   error.hidden   = true;

    try {
      const params = new URLSearchParams({ page: String(this._currentPage), limit: String(PAGE_SIZE) });
      if (this._currentState) params.set('state', this._currentState);
      if (this._currentCity) params.set('city', this._currentCity);

      const resp = await fetch(`${this._apiUrl}?${params}`);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);

      const { stores, page, totalPages, total } = await resp.json();
      this._currentPage = page;
      this._totalPages  = totalPages;

      if (loading) loading.hidden = true;
      this.#renderCityPage(stores, total);
    } catch {
      if (loading) loading.hidden = true;
      if (error)   error.hidden   = false;
    }
  }

  /**
   * @param {StoreData[]} stores
   * @param {number} total
   */
  #renderCityPage(stores, total) {
    const list     = this._refs.storeList;
    const template = this._refs.cardTemplate;
    if (!list || !(template instanceof HTMLTemplateElement)) return;

    list.innerHTML = '';
    const fragment = document.createDocumentFragment();
    for (const store of stores) {
      const card = this.#buildCard(template, store);
      if (card) fragment.append(card);
    }
    list.append(fragment);

    if (this._refs.resultCount) {
      const template = total === 1 ? this._i18n.countFoundOne : this._i18n.countFoundOther;
      this._refs.resultCount.textContent = this._currentCity
        ? (template ?? '').replace('__COUNT__', String(total))
        : '';
    }

    if (this._refs.noResults) {
      this._refs.noResults.hidden = stores.length > 0;
    }

    this.#updatePagination();
  }

  // ─── Event handlers ─────────────────────────────────────────────────────────

  /** @param {Event} _event */
  onStateChange(_event) {
    this._currentState = /** @type {HTMLSelectElement} */ (this._refs.stateFilter)?.value ?? '';
    this._currentCity = '';
    this._currentPage = 1;
    // Reset the city dropdown and repopulate it scoped to the chosen state
    this.#fetchCities(this._currentState);
    // Any dropdown interaction = browse mode (all stores paginated, GPS ignored)
    this.#fetchPage();
  }

  /** @param {Event} _event */
  onCityChange(_event) {
    this._currentCity = /** @type {HTMLSelectElement} */ (this._refs.cityFilter)?.value ?? '';
    this._currentPage = 1;
    // Any dropdown interaction = browse mode (all stores paginated, GPS ignored)
    this.#fetchPage();
  }

  /** @param {Event} _event */
  onFirstPage(_event) { this.#goToPage(1); }

  /** @param {Event} _event */
  onPrevPage(_event) {
    const current = this._mode === 'location' ? this._locationPage : this._currentPage;
    this.#goToPage(current - 1);
  }

  /** @param {Event} _event */
  onNextPage(_event) {
    const current = this._mode === 'location' ? this._locationPage : this._currentPage;
    this.#goToPage(current + 1);
  }

  /** @param {Event} _event */
  onLastPage(_event) {
    this.#goToPage(this._mode === 'location' ? this._locationTotalPages : this._totalPages);
  }

  /** @param {number} page 1-based target page (clamped to valid range) */
  #goToPage(page) {
    if (this._mode === 'location') {
      const target = Math.min(Math.max(1, page), this._locationTotalPages);
      if (target === this._locationPage) return;
      this._locationPage = target;
      this.#renderLocationPage();
    } else {
      const target = Math.min(Math.max(1, page), this._totalPages);
      if (target === this._currentPage) return;
      this._currentPage = target;
      this.#fetchPage();
    }
    this.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  // ─── Cities dropdown ────────────────────────────────────────────────────────

  async #fetchStates() {
    try {
      const resp = await fetch(`${this._apiUrl}/states`);
      if (!resp.ok) return;
      const { states } = await resp.json();
      const select = /** @type {HTMLSelectElement | null} */ (this._refs.stateFilter);
      if (!select) return;
      // US 2-letter codes first (alphabetical), then any non-US values last
      const isUsCode = (s) => /^[A-Z]{2}$/.test(s);
      states.sort((a, b) => (isUsCode(a) === isUsCode(b) ? a.localeCompare(b) : isUsCode(a) ? -1 : 1));
      for (const state of states) {
        const opt = document.createElement('option');
        opt.value = state;
        opt.textContent = state;
        select.append(opt);
      }
    } catch { /* ignore */ }
  }

  /** @param {string} [state] Scope the city list to a single state */
  async #fetchCities(state = '') {
    const select = /** @type {HTMLSelectElement | null} */ (this._refs.cityFilter);
    if (!select) return;

    // Keep only the "All cities" placeholder before repopulating
    while (select.options.length > 1) select.remove(1);
    select.value = '';

    try {
      const query = state ? `?state=${encodeURIComponent(state)}` : '';
      const resp = await fetch(`${this._apiUrl}/cities${query}`);
      if (!resp.ok) return;
      const { cities } = await resp.json();
      for (const city of cities) {
        const opt = document.createElement('option');
        opt.value = city;
        opt.textContent = city;
        select.append(opt);
      }
    } catch { /* ignore */ }
  }

  // ─── Card builder ───────────────────────────────────────────────────────────

  /**
   * @param {HTMLTemplateElement} template
   * @param {StoreData} store
   * @returns {HTMLLIElement | null}
   */
  #buildCard(template, store) {
    const clone = /** @type {DocumentFragment} */ (template.content.cloneNode(true));
    const li    = /** @type {HTMLLIElement | null} */ (clone.firstElementChild);
    if (!li) return null;

    li.dataset.city = (store.city ?? '').toLowerCase();
    li.dataset.lat  = String(store.latitude  ?? '');
    li.dataset.lng  = String(store.longitude ?? '');

    const nameEl = li.querySelector('.mut-store-card__name');
    if (nameEl) nameEl.textContent = store.name;

    const logo = store.brand ? this._brandLogos[store.brand] : null;
    if (logo && logo.url) {
      const logoEl = /** @type {HTMLImageElement | null} */ (li.querySelector('.mut-store-card__logo'));
      if (logoEl) {
        logoEl.src = logo.url;
        logoEl.alt = logo.alt || store.name;
        logoEl.hidden = false;
      }
    }

    if (store.address || store.city || store.state || store.postal_code) {
      const addressEl = li.querySelector('.mut-store-card__address');
      if (addressEl) {
        const street   = addressEl.querySelector('.mut-store-card__street');
        const locality = addressEl.querySelector('.mut-store-card__locality');
        const postal   = addressEl.querySelector('.mut-store-card__postal');
        if (street)   street.textContent   = store.address ?? '';
        if (locality) locality.textContent = [store.city, store.state].filter(Boolean).join(', ');
        if (postal)   postal.textContent   = store.postal_code ?? '';
        /** @type {HTMLElement} */ (addressEl).hidden = false;
      }
    }

    if (store.phone) {
      const phoneEl = li.querySelector('.mut-store-card__phone');
      if (phoneEl) {
        const link  = document.createElement('a');
        link.href   = `tel:${store.phone.replace(/[\s\-\(\)]/g, '')}`;
        link.textContent = store.phone;
        phoneEl.append(link);
        /** @type {HTMLElement} */ (phoneEl).hidden = false;
      }
    }

    if (store.hours) {
      const hoursEl   = li.querySelector('.mut-store-card__hours');
      const hoursText = li.querySelector('.mut-store-card__hours-text');
      if (hoursEl && hoursText) {
        hoursText.textContent = store.hours;
        /** @type {HTMLElement} */ (hoursEl).hidden = false;
      }
    }

    if (store.maps_url) {
      const dirEl = /** @type {HTMLAnchorElement | null} */ (li.querySelector('.mut-store-card__directions'));
      if (dirEl) {
        dirEl.href   = store.maps_url;
        dirEl.hidden = false;
      }
    }

    return li;
  }

  // ─── Pagination UI ──────────────────────────────────────────────────────────

  #updatePagination() {
    const pagination = /** @type {HTMLElement | null} */ (this._refs.pagination);
    if (!pagination) return;

    pagination.hidden = this._totalPages <= 1;

    const pageInfo = this._refs.pageInfo;
    if (pageInfo) pageInfo.textContent = `${this._currentPage} / ${this._totalPages}`;

    const firstBtn = /** @type {HTMLButtonElement | null} */ (this._refs.firstBtn);
    const prevBtn  = /** @type {HTMLButtonElement | null} */ (this._refs.prevBtn);
    const nextBtn  = /** @type {HTMLButtonElement | null} */ (this._refs.nextBtn);
    const lastBtn  = /** @type {HTMLButtonElement | null} */ (this._refs.lastBtn);
    const atFirst = this._currentPage === 1;
    const atLast  = this._currentPage === this._totalPages;
    if (firstBtn) firstBtn.disabled = atFirst;
    if (prevBtn)  prevBtn.disabled  = atFirst;
    if (nextBtn)  nextBtn.disabled  = atLast;
    if (lastBtn)  lastBtn.disabled  = atLast;
  }

  // ─── Haversine ──────────────────────────────────────────────────────────────

  /**
   * @param {number} lat1 @param {number} lon1 @param {number} lat2 @param {number} lon2
   * @returns {number} distance in km
   */
  #haversine(lat1, lon1, lat2, lon2) {
    const R    = 6371;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a    =
      Math.sin(dLat / 2) ** 2 +
      Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }
}

customElements.define('mut-store-finder', MutStoreFinder);
